import { useMemo } from 'react';
import PropTypes from 'prop-types';
import MaterialTable from '@material-table/core';
import { Box, Button, CircularProgress, Tooltip } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  DeleteOutline as DeleteOutlineIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';

import { CardWrapper } from 'globalStyles';
import { isEven } from 'utils/helpers';
import { colors } from 'themes';

const formatName = rowData => {
  const prefix = rowData.prefix ? `${rowData.prefix} ` : '';

  return `${prefix}${rowData.firstName} ${rowData.lastName}`;
};

const renderApprovalStatus = isApproved => {
  if (isApproved) {
    return <CheckCircleIcon color="success" titleAccess="Approved" fontSize="medium" />;
  }

  return <ScheduleIcon color="warning" titleAccess="Pending Approval" fontSize="medium" />;
};

const CustomTargetsTable = ({ customTargets, handleRemoveCustomTarget, isLoading }) => {
  const renderLoader = () => (
    <Box position="relative" display="inline-flex">
      <CircularProgress size={56} />
    </Box>
  );

  const columns = useMemo(
    () => [
      {
        customFilterAndSearch: (term, rowData) => {
          const fullName = formatName(rowData).toLowerCase();
          const office = (rowData.office || '').toLowerCase();
          const boundaryName = (rowData.boundary?.name || '').toLowerCase();
          const searchTerm = term.toLowerCase();

          return (
            fullName.includes(searchTerm) ||
            office.includes(searchTerm) ||
            boundaryName.includes(searchTerm)
          );
        },
        field: 'name',
        render: rowData => (
          <span style={{ fontWeight: 'bold' }}>{formatName(rowData) || 'N/A'}</span>
        ),
        title: 'Name',
        width: '25%',
      },
      {
        field: 'isApproved',
        render: rowData => renderApprovalStatus(rowData.isApproved),
        title: 'Status',
        width: '10%',
      },
      {
        field: 'office',
        title: 'Office Title',
        width: '25%',
      },
      {
        field: 'boundary',
        render: rowData => rowData.boundary?.name || 'N/A',
        title: 'Boundary',
        width: '25%',
      },
      {
        field: 'contactMethod',
        render: rowData => {
          if (!rowData.isApproved) {
            return <em style={{ color: 'gray' }}>Pending Approval</em>;
          }

          return rowData.contactMethod ? (
            <span style={{ color: '#1976d2' }}>{rowData.contactMethod}</span>
          ) : (
            'Petition-only'
          );
        },
        title: 'Contact method',
        width: '25%',
      },
      {
        align: 'right',
        render: rowData => (
          <Tooltip
            title={
              rowData.canDelete
                ? ''
                : 'This custom target is associated with a non-draft action center and cannot be deleted.'
            }
          >
            <span>
              <Button
                color="error"
                disabled={!rowData.canDelete}
                onClick={() => handleRemoveCustomTarget(rowData.uuid)}
                startIcon={<DeleteOutlineIcon />}
              >
                Delete
              </Button>
            </span>
          </Tooltip>
        ),
        sorting: false,
        title: '',
        width: '15%',
      },
    ],
    [handleRemoveCustomTarget]
  );

  return (
    <CardWrapper>
      <MaterialTable
        columns={columns}
        data={customTargets}
        localization={{
          body: {
            emptyDataSourceMessage: isLoading ? renderLoader() : 'No custom targets found',
          },
          toolbar: {
            searchPlaceholder: 'Search',
          },
        }}
        options={{
          actionsColumnIndex: -1,
          headerStyle: {
            color: colors.osloGray,
          },
          maxColumnSort: 0,
          pageSize: 10,
          pageSizeOptions: [10, 20, 50, 100],
          rowStyle: (_, rowNumber) => ({
            backgroundColor: isEven(rowNumber) ? colors.alabaster : colors.white,
            fontSize: '14px',
          }),
          searchDebounceDelay: 400,
          searchFieldAlignment: 'left',
          showFirstLastPageButtons: false,
          showTitle: false,
        }}
        style={{
          boxShadow: 'none',
        }}
      />
    </CardWrapper>
  );
};

CustomTargetsTable.propTypes = {
  customTargets: PropTypes.arrayOf(
    PropTypes.shape({
      boundary: PropTypes.shape({
        boundaryType: PropTypes.string,
        name: PropTypes.string,
        uuid: PropTypes.string,
      }),
      canDelete: PropTypes.bool,
      contactMethod: PropTypes.string,
      firstName: PropTypes.string,
      isApproved: PropTypes.bool,
      lastName: PropTypes.string,
      office: PropTypes.string,
      prefix: PropTypes.string,
      uuid: PropTypes.string,
    })
  ),
  handleRemoveCustomTarget: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

CustomTargetsTable.defaultProps = {
  customTargets: [],
  isLoading: false,
};

export default CustomTargetsTable;
